/** Client-side sample captions for onboarding preview (no AI credits / paid API). */
export const DEFAULT_ONBOARDING_TONE = 'egitici' as const;
export const DEFAULT_ONBOARDING_TOPIC = 'teknoloji' as const;

export const ONBOARDING_TONES = [
  { id: 'profesyonel', labelKey: 'onboarding.tones.profesyonel' },
  { id: 'samimi', labelKey: 'onboarding.tones.samimi' },
  { id: 'espritli', labelKey: 'onboarding.tones.espritli' },
  { id: 'egitici', labelKey: 'onboarding.tones.egitici' },
] as const;

export const ONBOARDING_TOPICS = [
  { id: 'lifestyle', labelKey: 'onboarding.topics.lifestyle' },
  { id: 'business', labelKey: 'onboarding.topics.business' },
  { id: 'yaraticilik', labelKey: 'onboarding.topics.yaraticilik' },
  { id: 'teknoloji', labelKey: 'onboarding.topics.teknoloji' },
] as const;

export type OnboardingToneId = (typeof ONBOARDING_TONES)[number]['id'];
export type OnboardingTopicId = (typeof ONBOARDING_TOPICS)[number]['id'];

const SAMPLES: Record<OnboardingToneId, Record<OnboardingTopicId, string>> = {
  profesyonel: {
    lifestyle: '🌅 Sabahlar, azimli ve hedefli başlarsan, gün sana ait olur. Bugün ne yapacaksın?',
    business: '📊 Rakamlar konuşur. Geçen ayda %35 büyüdük. Başarı, sabırla ve strateji ile gelir.',
    yaraticilik: '🎨 Her renk, hikaye anlatır. Renkli düşünmek, renkli yaşamaktır.',
    teknoloji: '⚙️ Teknoloji, hayatı kolaylaştırır. Ama insan dokunuşu her zaman gereklidir.',
  },
  samimi: {
    lifestyle: 'Harika bir sabah oldu bu sabah ☀️ Sabahki kahve, akşamın tatlısı 😄',
    business: 'Dün 3 yeni proje başlattık! Ekip olunca her şey başarılı oluyor 💪',
    yaraticilik: 'Sanat yapmak beni mutlu ediyor. Yaptığın şey seni anlatıyor 🎭',
    teknoloji: 'AI ile çalışmaya başladık! Gelecek çok heyecan verici 🚀',
  },
  espritli: {
    lifestyle: 'Yaşam, önemli olan şeylerin bir listesidir. İşte ben de ona dahilim ✨',
    business: 'Para güzel, ama bağlantılar daha güzel. Ağını genişlet! 🕸️',
    yaraticilik: 'Sanat demek özgürlük demek. İçindeki sanatçıyı aç! 🎪',
    teknoloji: 'Kod yazması gibi, yaşaması da sanat 💻✨',
  },
  egitici: {
    lifestyle: '💡 Biliyor musun? Sabah alışkanlıkların gün boyu verimliliğini %50 artırır.',
    business: '📖 Girişimcilik notu: İlk müşterin kimse olmamalı — öğrenmen gerekir.',
    yaraticilik: '🎓 Bilinmesi gereken: Yaratıcılık pratikte gelişir. Teoride değil.',
    teknoloji: '🔬 Teknik bilgi: İyi kod, kütüphaneler gibidir — tasarlanır, yapılmaz.',
  },
};

export function sampleCaption(tone: string, topic: string): string {
  const byTone = SAMPLES[tone as OnboardingToneId];
  if (!byTone) return '';
  return byTone[topic as OnboardingTopicId] ?? '';
}

const STORAGE_KEY = 'loopcast_onboarding';

const RESUMABLE_STEPS = new Set([
  '/onboarding',
  '/onboarding/tone',
  '/onboarding/preview',
  '/onboarding/connect',
  '/select-plan',
]);

export interface OnboardingDraft {
  tone: string;
  topic: string;
  step: string;
  userId?: string;
}

function readStored(): Partial<OnboardingDraft> | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY) ?? sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as Partial<OnboardingDraft>;
  } catch {
    return null;
  }
}

function writeStored(draft: OnboardingDraft): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(draft));
  sessionStorage.removeItem(STORAGE_KEY);
}

export function loadOnboardingDraft(userId?: string): OnboardingDraft {
  const parsed = readStored();
  if (!parsed) {
    return { tone: DEFAULT_ONBOARDING_TONE, topic: DEFAULT_ONBOARDING_TOPIC, step: '/onboarding' };
  }
  if (userId && parsed.userId && parsed.userId !== userId) {
    return { tone: DEFAULT_ONBOARDING_TONE, topic: DEFAULT_ONBOARDING_TOPIC, step: '/onboarding' };
  }
  const step = typeof parsed.step === 'string' && RESUMABLE_STEPS.has(parsed.step) ? parsed.step : '/onboarding';
  return {
    tone: parsed.tone || DEFAULT_ONBOARDING_TONE,
    topic: parsed.topic || DEFAULT_ONBOARDING_TOPIC,
    step,
    userId: parsed.userId,
  };
}

export function saveOnboardingDraft(draft: Pick<OnboardingDraft, 'tone' | 'topic'>, userId?: string): void {
  const prev = loadOnboardingDraft(userId);
  writeStored({
    tone: draft.tone,
    topic: draft.topic,
    step: prev.step || '/onboarding',
    userId: userId ?? prev.userId,
  });
}

/** Persist the current onboarding route so login / revisit resumes here (browser only). */
export function saveOnboardingStep(step: string, userId?: string): void {
  if (!RESUMABLE_STEPS.has(step)) return;
  const prev = loadOnboardingDraft(userId);
  writeStored({
    tone: prev.tone,
    topic: prev.topic,
    step,
    userId: userId ?? prev.userId,
  });
}

export function getOnboardingResumePath(userId?: string): string {
  return loadOnboardingDraft(userId).step || '/onboarding';
}

export function clearOnboardingDraft(): void {
  localStorage.removeItem(STORAGE_KEY);
  sessionStorage.removeItem(STORAGE_KEY);
}
